import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Check } from "lucide-react";

/**
 * Minimalist CustomDropdown matching the ModelManagement design theme.
 * Uses createPortal and dynamic position calculation to avoid truncation by overflow-hidden containers.
 */
const CustomDropdown = ({
  options = [],
  value,
  onChange,
  placeholder = "Select option",
  disabled = false,
  className = "",
  buttonClassName = "",
  align = "left",
  renderOption,
  renderSelected,
  showChevron = true,
  menuWidth,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const portalId = useRef(`custom-dd-portal-${Math.random().toString(36).substring(2, 9)}`);
  const [menuCoords, setMenuCoords] = useState({
    top: 0,
    left: 0,
    width: 0,
    openUp: false,
    maxHeight: 240,
  });

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
    }
  }, [isOpen]);

  const getOptionVal = useCallback((opt) => {
    if (!opt) return undefined;
    return opt.value !== undefined ? opt.value : opt.id;
  }, []);

  const getOptionLabel = useCallback((opt) => {
    if (!opt) return "";
    return opt.label !== undefined ? opt.label : (opt.name !== undefined ? opt.name : String(opt));
  }, []);

  const updatePosition = useCallback(() => {
    if (!dropdownRef.current) return;
    const rect = dropdownRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const estimatedHeight = Math.min(options.length * 36 + 12, 240);

    const openUp = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;
    const maxHeight = Math.max(120, Math.min(openUp ? spaceAbove - 16 : spaceBelow - 16, 240));

    setMenuCoords({
      top: openUp ? rect.top + window.scrollY : rect.bottom + window.scrollY + 6,
      left: rect.left + window.scrollX,
      width: menuWidth || Math.max(rect.width, 180),
      openUp,
      maxHeight,
    });
  }, [options.length, menuWidth]);

  useEffect(() => {
    if (!isOpen) return;

    updatePosition();

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        const portalEl = document.getElementById(portalId.current);
        if (portalEl && portalEl.contains(event.target)) return;
        setIsOpen(false);
      }
    };

    const handleScrollOrResize = () => {
      updatePosition();
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [isOpen, updatePosition]);

  const selectedOption = options.find((opt) => getOptionVal(opt) === value)
    || options.find((opt) => getOptionVal(opt) === "")
    || (value === "" || value === null || value === undefined ? null : options[0]);

  const displayLabel = selectedOption
    ? renderSelected
      ? renderSelected(selectedOption)
      : getOptionLabel(selectedOption)
    : placeholder;

  const filteredOptions = searchTerm.trim()
    ? options.filter((opt) =>
        getOptionLabel(opt).toLowerCase().includes(searchTerm.trim().toLowerCase())
      )
    : options;

  return (
    <div ref={dropdownRef} className={`relative w-full ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!disabled) {
            if (!isOpen) updatePosition();
            setIsOpen(!isOpen);
          }
        }}
        className={`w-full px-3.5 py-2.5 bg-white border text-left text-xs font-medium rounded-xl transition-all flex items-center justify-between outline-none ${
          disabled
            ? "opacity-40 cursor-not-allowed bg-zinc-50/50 border-zinc-200"
            : isOpen
            ? "border-zinc-900 shadow-sm ring-1 ring-zinc-900/5 bg-white"
            : "border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50/40 text-zinc-900 cursor-pointer"
        } ${buttonClassName}`}
      >
        <span className={`truncate ${selectedOption ? "text-zinc-900 font-medium" : "text-zinc-400"}`}>
          {displayLabel}
        </span>
        {showChevron && (
          <ChevronDown
            size={14}
            className={`shrink-0 transition-transform duration-200 text-zinc-400 ml-2 ${
              isOpen ? "rotate-180 text-zinc-900" : ""
            }`}
          />
        )}
      </button>

      {isOpen &&
        !disabled &&
        createPortal(
          <div
            id={portalId.current}
            style={{
              position: "absolute",
              top: menuCoords.openUp ? "auto" : `${menuCoords.top}px`,
              bottom: menuCoords.openUp
                ? `${document.documentElement.scrollHeight - menuCoords.top + 6}px`
                : "auto",
              left: `${menuCoords.left}px`,
              width: `${menuCoords.width}px`,
              maxHeight: `${menuCoords.maxHeight}px`,
              zIndex: 99999,
            }}
            className="flex flex-col bg-white border border-zinc-200/90 rounded-xl shadow-xl shadow-zinc-950/10 custom-scrollbar animate-fadeIn overflow-hidden"
          >
            {options.length > 6 && (
              <div className="p-2 border-b border-zinc-100 bg-zinc-50/70 sticky top-0 z-10 shrink-0">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search options..."
                  className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-xs text-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 font-medium"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}
            <div className="py-1 overflow-y-auto custom-scrollbar flex-1">
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2.5 text-xs text-zinc-400 text-center">No matching options</div>
              ) : (
                filteredOptions.map((option, idx) => {
                  const optVal = getOptionVal(option);
                  const optLabel = getOptionLabel(option);
                  const isSelected = value === optVal;
                  const isOptDisabled = Boolean(option.disabled);

                  return (
                    <button
                      type="button"
                      key={optVal !== undefined ? optVal : idx}
                      disabled={isOptDisabled}
                      onClick={() => {
                        if (isOptDisabled) return;
                        onChange(optVal);
                        setIsOpen(false);
                      }}
                      className={`w-full px-3.5 py-2 text-xs text-left flex items-center justify-between transition-colors ${
                        isOptDisabled
                          ? "text-zinc-300 cursor-not-allowed bg-zinc-50/50"
                          : isSelected
                          ? "bg-zinc-100 text-zinc-900 font-semibold"
                          : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                      }`}
                    >
                      <span className="truncate">
                        {renderOption ? renderOption(option, isSelected) : optLabel}
                      </span>
                      {isSelected && <Check size={13} className="text-zinc-900 shrink-0 ml-2" />}
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default CustomDropdown;
